import { useState } from 'react';
import { toast } from 'react-toastify';
import AddressGeoApi from 'components/AddressGeoApi/AddressGeoApi';
import Button from 'components/button';
import ModalBody from 'components/modal/components/body';
import ModalFooter from 'components/modal/components/footer';
import Modalheader from 'components/modal/components/header';
import useModal from 'components/modal/useModal';
import {
  BILLING_INFORMATION_UPDATED,
  formatAddressLine,
} from 'components/SlideOver/dataSlideOver/data/slideOverCompany/CompanyBillingInformationCard';
import StyledInput from 'components/styledInput/StyledInput';
import {
  PAYMENT_TERMS,
  findPaymentTerm,
  paymentTermLabel,
} from 'constants/paymentTerms';
import UseContext from 'hooks/useContext';
import ApiDatabase from 'server';

const comparable = (value) =>
  String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

/** The api's rule: same street, postcode and city, whatever the case and spacing. */
const sameAddress = (a, b) =>
  Boolean(a && b) &&
  ['street', 'postcode', 'city'].every(
    (field) => comparable(a[field]) === comparable(b[field]),
  );

/** What the api reads of an address; AddressGeoApi also hands a complement it does not store. */
const toBillingAddressPayload = (address) =>
  Object.fromEntries(
    ['street', 'postcode', 'city', 'department', 'lon', 'lat']
      .filter(
        (field) =>
          address?.[field] !== undefined &&
          address?.[field] !== null &&
          address?.[field] !== '',
      )
      .map((field) => [field, address[field]]),
  );

// The api refusals that belong to one field, shown under it.
const FIELD_ERRORS = {
  BILLING_ADDRESS_INVALID: {
    field: 'address',
    fallback: 'Adresse incomplète : rue, code postal à 5 chiffres et ville.',
  },
  PAYMENT_TERM_INVALID: {
    field: 'paymentTerm',
    fallback: 'Délai de paiement invalide.',
  },
};

/** "Informations de facturation" of a company (JBWEB-1268, mockups 8.2 and 8.2b). */
const ModalCompanyBillingInformation = () => {
  const { toggle } = useModal();
  const { language, modalData, setModalDataReturn } = UseContext();
  const text = (key, fallback) =>
    language.getValue(`companyBilling.${key}`, fallback);

  const idCompany = modalData?.idCompany;
  const initial = modalData?.billingInformation ?? {};
  const companyAddress = initial.companyAddress ?? null;
  const initialFollows = initial.followsCompanyAddress !== false;
  const initialTermKey = findPaymentTerm(initial.paymentTerm)?.key ?? '';

  const [follows, setFollows] = useState(initialFollows);
  const [customAddress, setCustomAddress] = useState(
    initialFollows ? null : (initial.billingAddress ?? null),
  );
  // What the address field shows. AddressGeoApi hands back every address it is
  // given, this one included, so `pickAddress` sorts the echo from a choice.
  const [shownAddress, setShownAddress] = useState(
    initialFollows ? companyAddress : (initial.billingAddress ?? null),
  );
  const [termKey, setTermKey] = useState(initialTermKey);
  const [errors, setErrors] = useState({});
  const [isSending, setIsSending] = useState(false);

  const pickAddress = (address) => {
    setErrors((previous) => ({ ...previous, address: null }));
    if (sameAddress(address, companyAddress)) {
      setFollows(true);
      setCustomAddress(null);
      return;
    }
    setFollows(false);
    setCustomAddress(address);
  };

  const restoreCompanyAddress = () => {
    setFollows(true);
    setCustomAddress(null);
    // A new object: AddressGeoApi only takes a value that is not the one it holds.
    setShownAddress(companyAddress ? { ...companyAddress } : null);
    setErrors((previous) => ({ ...previous, address: null }));
  };

  const addressChanged =
    follows !== initialFollows ||
    (!follows && !sameAddress(customAddress, initial.billingAddress));
  const hasChanges = addressChanged || termKey !== initialTermKey;
  const canSave =
    hasChanges && !isSending && (follows || Boolean(customAddress));

  const save = () => {
    if (!canSave) return;
    const term =
      PAYMENT_TERMS.find((candidate) => candidate.key === termKey) ?? null;
    setIsSending(true);
    ApiDatabase.putCompanyBillingInformation(
      {
        idCompany,
        followsCompanyAddress: follows,
        paymentTerm: term
          ? { days: term.days, endOfMonth: term.endOfMonth }
          : null,
        ...(follows
          ? {}
          : { billingAddress: toBillingAddressPayload(customAddress) }),
      },
      (response) => {
        setIsSending(false);
        if (response?.besttSync === 'failed') {
          toast.warning(
            text(
              'savedBesttFailed',
              'Enregistré, mais BeSTT n’a pas été mis à jour : les factures imprimeront l’ancienne adresse.',
            ),
          );
        } else {
          toast.success(
            text('saved', 'Informations de facturation enregistrées.'),
          );
        }
        setModalDataReturn({
          type: BILLING_INFORMATION_UPDATED,
          billingInformation: response?.data,
        });
        toggle();
      },
      (error) => {
        if (error?.name === 'CanceledError') return;
        setIsSending(false);
        // Never the error object itself in the console: axios keeps the
        // request, the admin's token included.
        const code = error?.response?.data?.errorCode;
        const fieldError = FIELD_ERRORS[code];
        if (fieldError) {
          setErrors({
            [fieldError.field]: text(`errors.${code}`, fieldError.fallback),
          });
          return;
        }
        if (code === 'COMPANY_NOT_FOUND') {
          toast.error(
            text('errors.COMPANY_NOT_FOUND', 'Entreprise introuvable.'),
          );
          toggle();
          return;
        }
        toast.error(
          text('errors.generic', 'Une erreur est survenue, réessayez.'),
        );
      },
    );
  };

  const companyAddressLine = formatAddressLine(companyAddress);
  const initialTermLabel = paymentTermLabel(initial.paymentTerm, language);
  const currentTermHint = initialTermLabel
    ? text('currentPaymentTerm', 'Actuellement : {term}').replace(
        '{term}',
        () => initialTermLabel,
      )
    : text('currentPaymentTermNone', 'Actuellement : non renseigné');

  const renderAddressHint = () => {
    if (!companyAddressLine) {
      return text('noCompanyAddress', 'L’entreprise n’a pas d’adresse.');
    }
    if (follows) return text('companyAddressHint', 'Adresse de l’entreprise');
    return (
      <>
        {/* A function, not a string, as the replacement: a "$&" in an
            address would otherwise be read as a pattern. */}
        {text(
          'companyAddressPrefix',
          'Adresse de l’entreprise : {address}',
        ).replace('{address}', () => companyAddressLine)}
        {' · '}
        <button
          type={'button'}
          className={'underline text-gray-900'}
          onClick={restoreCompanyAddress}
          disabled={isSending}
        >
          {text('restore', 'Rétablir')}
        </button>
      </>
    );
  };

  return (
    <>
      <Modalheader hide={toggle} hideShow={!isSending}>
        {text('modalTitle', 'Informations de facturation')}
      </Modalheader>
      <ModalBody>
        <div className={'flex flex-col gap-4'}>
          <div className={'ft-sm text-gray-500'}>
            {text(
              'modalIntro',
              'S’appliquent à toutes les factures de l’entreprise. Une facture peut avoir un délai différent, depuis son détail.',
            )}
          </div>
          <div className={'flex flex-col gap-1'}>
            {text('billingAddress', 'Adresse de facturation')}
            <AddressGeoApi
              addressGeoValue={pickAddress}
              addressValue={shownAddress}
              page={'editCompany'}
            />
            {errors.address ? (
              <div className={'text-red-500 text-xs'}>{errors.address}</div>
            ) : (
              <div className={'text-gray-500 text-xs'}>
                {renderAddressHint()}
              </div>
            )}
          </div>
          <div className={'border-t border-gray-100'} />
          <StyledInput
            type={'select'}
            label={text('paymentTerm', 'Délai de paiement')}
            value={termKey}
            placeholder={text('paymentTermPlaceholder', 'Choisir un délai')}
            list={PAYMENT_TERMS.map((term) => ({
              value: term.key,
              label: text(`paymentTerms.${term.key}`, term.defaultLabel),
            }))}
            onChange={(event) => {
              setTermKey(event.target.value);
              setErrors((previous) => ({ ...previous, paymentTerm: null }));
            }}
            error={errors.paymentTerm}
            hint={currentTermHint}
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          size={'LG'}
          color={isSending ? 'DISABLED' : 'SECONDARY'}
          disabled={isSending}
          onClick={toggle}
        >
          {text('cancel', 'Annuler')}
        </Button>
        <Button
          size={'LG'}
          color={canSave ? 'PRIMARY' : 'DISABLED'}
          disabled={!canSave}
          onClick={save}
        >
          {isSending
            ? text('saving', 'Enregistrement…')
            : text('save', 'Enregistrer')}
        </Button>
      </ModalFooter>
    </>
  );
};

export default ModalCompanyBillingInformation;
